import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { create } from 'zustand';

export interface NavItem {
    id: string;
    label: string;
    module?: string;
    url: string;
    icon?: IconDefinition;
    /** Synonyms/keywords used by Cmd+K search to match queries the label doesn't contain. */
    tags?: string[];
    demoted?: boolean;
}

export interface Command {
    id: string;
    label: string;
    module?: string;
    icon?: IconDefinition;
    /** Synonyms/keywords used by Cmd+K search to match queries the label doesn't contain. */
    tags?: string[];
    onSelect: () => void;
}

interface CommandPaletteStore {
    isOpen: boolean;
    actionMode: boolean;
    open: () => void;
    openInActionMode: () => void;
    close: () => void;
    toggle: () => void;
    navItems: NavItem[];
    commands: Command[];
    setNavItems: (items: NavItem[]) => void;
    /** Register additional nav items (e.g. settings sections) without replacing the full list. */
    registerNavItems: (items: NavItem[]) => void;
    unregisterNavItems: (ids: string[]) => void;
    registerCommands: (cmds: Command[]) => void;
    unregisterCommands: (ids: string[]) => void;
}

// Shallow-compares two nav lists so store updates can be skipped when nothing changed.
const navItemsEqual = (a: NavItem[], b: NavItem[]) =>
    a.length === b.length &&
    a.every(
        (item, index) =>
            item.id === b[index].id &&
            item.label === b[index].label &&
            item.module === b[index].module &&
            item.url === b[index].url &&
            item.icon === b[index].icon &&
            (item.tags?.join('|') ?? '') === (b[index].tags?.join('|') ?? '')
    );

// Shallow-compares two command lists so store updates can be skipped when nothing changed.
const commandsEqual = (a: Command[], b: Command[]) =>
    a.length === b.length &&
    a.every(
        (item, index) =>
            item.id === b[index].id &&
            item.label === b[index].label &&
            item.module === b[index].module &&
            item.icon === b[index].icon &&
            (item.tags?.join('|') ?? '') === (b[index].tags?.join('|') ?? '')
    );

const useCommandPaletteStore = create<CommandPaletteStore>()((set) => ({
    isOpen: false,
    actionMode: false,
    open: () => set({ isOpen: true }),
    openInActionMode: () => set({ isOpen: true, actionMode: true }),
    close: () => set({ isOpen: false, actionMode: false }),
    toggle: () => set((state) => ({ isOpen: !state.isOpen, actionMode: false })),
    navItems: [],
    commands: [],
    setNavItems: (items) =>
        set((state) => {
            // Preserve items registered independently of the page nav (e.g. settings sections).
            // These have a stable id prefix and must survive the setNavItems replacement cycle
            // that fires whenever the page tree updates (e.g. SWR data arriving in ModulePageLayout).
            const incoming = new Set(items.map((i) => i.id));
            const preserved = state.navItems.filter(
                (n) => n.id.startsWith('settings-section::') && !incoming.has(n.id)
            );
            const newItems = preserved.length ? [...items, ...preserved] : items;
            return navItemsEqual(state.navItems, newItems) ? state : { navItems: newItems };
        }),
    registerNavItems: (items) =>
        set((state) => {
            const navItems = [...state.navItems.filter((n) => !items.find((ni) => ni.id === n.id)), ...items];
            return navItemsEqual(state.navItems, navItems) ? state : { navItems };
        }),
    unregisterNavItems: (ids) =>
        set((state) => {
            const navItems = state.navItems.filter((n) => !ids.includes(n.id));
            return navItems.length === state.navItems.length ? state : { navItems };
        }),
    registerCommands: (cmds) =>
        set((state) => {
            const commands = [...state.commands.filter((c) => !cmds.find((nc) => nc.id === c.id)), ...cmds];
            return commandsEqual(state.commands, commands) ? state : { commands };
        }),
    unregisterCommands: (ids) =>
        set((state) => {
            const commands = state.commands.filter((c) => !ids.includes(c.id));
            return commands.length === state.commands.length ? state : { commands };
        })
}));

export default useCommandPaletteStore;
